// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { absoluteUrl, cn } from "@/utils/common.utils";
import { usePostUploadImage } from "@/hooks/useFiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Matches the server's `/uploads/image` cap, so a big file fails before it uploads.
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_IMAGE_EDGE = 2000;

/** Re-draw a photo at most 2000px on its long edge as a JPEG. A pro listing
 *  photo drops from ~6MB to a few hundred KB, which is what every board card
 *  downloads. The server re-encodes again and strips EXIF either way. */
async function shrinkImage(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext("2d");
  // JPEG has no transparency; without this a transparent PNG turns black.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
  return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
}

export default function ImageDrop({ value, onChange, label = "Drop an image", helperText, aspect = "video", className, testId }) {
  const inp = useRef(null);
  const [hover, setHover] = useState(false);
  const [shrinking, setShrinking] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  // Label while the parent's onChange (which may return a promise) is saving.
  const [saving, setSaving] = useState("");
  const { mutate: uploadImage, isPending } = usePostUploadImage();
  const busy = shrinking || isPending || !!saving;
  const busyLabel = saving || "Uploading…";

  /** Hand the URL to the parent and wait for it to save. Resolves false on failure
   *  (the parent's mutation already shows the error toast). */
  const commit = async (url, label) => {
    setSaving(label);
    try {
      await onChange(url);
      return true;
    } catch {
      return false;
    } finally {
      setSaving("");
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Images only"); return; }
    if (file.size > MAX_IMAGE_BYTES) { toast.error("Photo is too large — max 8MB"); return; }
    let small;
    setShrinking(true);
    try {
      small = await shrinkImage(file);
    } catch {
      // HEIC on desktop Chrome, or a broken file: the browser can't decode it.
      toast.error("Couldn't read that photo — use a JPG, PNG or WEBP");
      return;
    } finally {
      setShrinking(false);
    }
    uploadImage(small, { onSuccess: (res) => commit(res.data.url, "Saving…") });
  };

  const onDrop = (e) => {
    e.preventDefault(); setHover(false);
    if (!busy) upload(e.dataTransfer.files?.[0]);
  };

  const aspectCls = aspect === "square" ? "aspect-square" : "aspect-video";

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={onDrop}
        onClick={() => { if (!busy) inp.current?.click(); }}
        aria-busy={busy || undefined}
        className={cn("relative border-2 border-dashed transition-colors", busy ? "cursor-wait" : "cursor-pointer", aspectCls, hover ? "border-primary bg-dash-hover" : "border-dash-border bg-white hover:bg-dash-page", className)}
        data-testid={testId || "image-drop"}
      >
        {value ? (
          <>
            <img src={absoluteUrl(value)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            {busy && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink/50 text-linen" data-testid={`${testId}-busy`}>
                <Loader2 className="size-6 animate-spin" />
                <span className="text-sm font-medium">{busyLabel}</span>
              </div>
            )}
            {/* text-linen here: the seamless variant's hover:text-current inherits it. */}
            <div className="absolute top-2 right-2 flex gap-2 text-linen" onClick={(e) => e.stopPropagation()}>
              <Button variant="seamless"
                type="button"
                onClick={() => inp.current?.click()}
                disabled={busy}
                className="gap-1.5 bg-ink/70 px-2.5 py-1.5 text-xs font-medium text-linen hover:bg-ink"
                data-testid={`${testId}-replace`}
              >
                <Upload className="size-3.5" /> Replace photo
              </Button>
              <Button variant="seamless"
                type="button"
                onClick={() => setConfirmRemove(true)}
                disabled={busy}
                aria-label="Remove photo"
                className="bg-ink/70 p-1.5 text-linen hover:bg-dash-rust"
                data-testid={`${testId}-clear`}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-stone">
            {busy ? <Loader2 className="size-6 animate-spin" /> : <span className="flex size-10 items-center justify-center rounded-xl bg-dash-page"><ImageIcon className="size-5" /></span>}
            <div className="text-sm font-medium text-dash-ink">{busy ? busyLabel : label}</div>
            <div className="text-xs text-dash-faint">{helperText || "Drag & drop or click"}</div>
          </div>
        )}
      </div>
      {/* Reset after each pick so choosing the same file again still fires. */}
      {/* className, not the `hidden` attribute: Input's own `flex` overrides that. */}
      <Input ref={inp} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testId}-input`} />
      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title="Remove this photo?"
        description="It will no longer show anywhere it was used. You can upload a new one any time."
        confirmLabel="Remove photo"
        icon={Trash2}
        // ConfirmDialog keeps its button spinning while this awaits.
        onConfirm={async () => { if (await commit("", "Removing…")) setConfirmRemove(false); }}
        testId={`${testId}-remove-confirm`}
      />
    </div>
  );
}

export function MultiImageDrop({ values, onChange, testId, max = 8, className, itemClassName, showMeta = true }) {
  const inp = useRef(null);
  const [busy, setBusy] = useState(false);
  const { mutateAsync: uploadImage } = usePostUploadImage();

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).slice(0, max - values.length);
    if (list.length === 0) return;
    setBusy(true);
    try {
      const results = await Promise.all(list.map(async (f) => {
        if (!f.type.startsWith("image/")) return null;
        const res = await uploadImage(f);
        return res.data.url;
      }));
      onChange([...values, ...results.filter(Boolean)]);
      toast.success(`${results.filter(Boolean).length} added`);
    } catch {
      // The mutation hook displays error.message.
    } finally { setBusy(false); }
  };

  const onDrop = (e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); };

  return (
    <div>
      <div className={cn("flex flex-wrap gap-2", className)} data-testid={testId}>
        {values.map((u, i) => (
          <div key={u + i} className={cn("group relative aspect-square size-32 border border-edge bg-bone sm:size-44", itemClassName)}>
            <img src={absoluteUrl(u)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <Button variant="seamless"
              type="button"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
              className="absolute top-1.5 right-1.5 bg-ink/70 text-linen p-1 opacity-0 group-hover:opacity-100 hover:bg-ink"
              data-testid={`${testId}-remove-${i}`}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ))}
        {values.length < max && (
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inp.current?.click()}
            className={cn("flex aspect-square size-32 cursor-pointer flex-col items-center justify-center gap-1 border-2 border-dashed border-dash-border bg-white text-xs text-dash-muted hover:bg-dash-page sm:size-44", itemClassName)}
            data-testid={`${testId}-add`}
          >
            {busy ? <Loader2 className="size-5 animate-spin" /> : <Plus className="size-5" />}
            <span>Add</span>
          </div>
        )}
      </div>
      <Input ref={inp} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid={`${testId}-input`} />
      {showMeta && <div className="overline mt-3">{values.length}/{max} photos · drag &amp; drop multiple</div>}
    </div>
  );
}
